import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { DAYS, FREQUENCIES, LENGTHS, TIMEZONES, maxDaysFor } from "@/lib/schedule";

export type ScheduleRow = {
  id: string;
  name: string;
  timezone: string;
  frequency: string;
  days_of_week: number[];
  time_of_day: string;
  length_words: number;
  publish_automatically: boolean;
  active: boolean;
  editorial_hint: string | null;
  keyword: string | null;
  next_run_at: string | null;
};

type Props = { editing: ScheduleRow | null; onDone: () => void; onCancel: () => void };

export function CreateScheduleCard({ editing, onDone, onCancel }: Props) {
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("America/New_York");
  const [frequency, setFrequency] = useState("1x_week");
  const [days, setDays] = useState<number[]>([1]);
  const [time, setTime] = useState("09:00");
  const [length, setLength] = useState(1500);
  const [keyword, setKeyword] = useState("");
  const [autoPublish, setAutoPublish] = useState(true);
  const [active, setActive] = useState(true);
  const [hint, setHint] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    setName(editing.name);
    setTimezone(editing.timezone);
    setFrequency(editing.frequency);
    setDays(editing.days_of_week ?? []);
    setTime(editing.time_of_day?.slice(0, 5) ?? "09:00");
    setLength(editing.length_words);
    setKeyword(editing.keyword ?? "");
    setAutoPublish(editing.publish_automatically);
    setActive(editing.active);
    setHint(editing.editorial_hint ?? "");
  }, [editing]);

  const limit = maxDaysFor(frequency);

  const pickFrequency = (value: string) => {
    setFrequency(value);
    const max = maxDaysFor(value);
    if (value === "daily") setDays([0, 1, 2, 3, 4, 5, 6]);
    else setDays((prev) => prev.slice(0, max));
  };

  const toggleDay = (d: number) => {
    setDays((prev) => {
      if (prev.includes(d)) return prev.filter((x) => x !== d);
      if (prev.length >= limit) return [...prev.slice(1), d];
      return [...prev, d].sort();
    });
  };

  const reset = () => {
    setName(""); setTimezone("America/New_York"); setFrequency("1x_week");
    setDays([1]); setTime("09:00"); setLength(1500); setKeyword("");
    setAutoPublish(true); setActive(true); setHint("");
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (days.length === 0) { toast.error("Pick at least one day of the week"); return; }
    setSaving(true);
    const payload = {
      name: name.trim(),
      timezone,
      frequency,
      days_of_week: days,
      time_of_day: `${time}:00`,
      length_words: length,
      keyword: keyword.trim() || null,
      publish_automatically: autoPublish,
      active,
      editorial_hint: hint.trim() || null,
    };
    const { error } = editing
      ? await supabase.from("blog_schedules").update(payload).eq("id", editing.id)
      : await supabase.from("blog_schedules").insert(payload);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(editing ? "Schedule updated" : "Schedule created");
    reset();
    onDone();
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h3 className="font-display text-xl font-bold mb-6">
        {editing ? "Edit schedule" : "New schedule"}
      </h3>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <Label htmlFor="sch-name" className="text-xs uppercase tracking-wider text-muted-foreground">Schedule name (internal)</Label>
          <Input id="sch-name" className="mt-1.5" placeholder="Weekly autopilot" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="sch-tz" className="text-xs uppercase tracking-wider text-muted-foreground">Timezone</Label>
          <select
            id="sch-tz"
            className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-6">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Frequency</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {FREQUENCIES.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => pickFrequency(f.value)}
              className={cn(
                "rounded-full border px-4 py-1.5 text-sm transition-colors",
                frequency === f.value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background hover:border-primary/50",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {DAYS.map((d) => (
            <button
              key={d.value}
              type="button"
              disabled={frequency === "daily"}
              onClick={() => toggleDay(d.value)}
              className={cn(
                "rounded-full border px-4 py-1.5 text-sm transition-colors disabled:opacity-60",
                days.includes(d.value)
                  ? "border-primary text-primary bg-primary/10"
                  : "border-border bg-background hover:border-primary/50",
              )}
            >
              {d.short}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {frequency === "daily" ? "Runs every day." : `Select up to ${limit} day${limit > 1 ? "s" : ""}.`}
        </p>
      </div>

      <div className="mt-6 grid gap-5 md:grid-cols-3">
        <div>
          <Label htmlFor="sch-time" className="text-xs uppercase tracking-wider text-muted-foreground">Time</Label>
          <Input id="sch-time" type="time" className="mt-1.5" value={time} onChange={(e) => setTime(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="sch-len" className="text-xs uppercase tracking-wider text-muted-foreground">Length</Label>
          <select
            id="sch-len"
            className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={length}
            onChange={(e) => setLength(Number(e.target.value))}
          >
            {LENGTHS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>
        <div className="flex flex-col justify-end gap-3 pb-1">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={autoPublish} onCheckedChange={(v) => setAutoPublish(!!v)} />
            Publish automatically
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={active} onCheckedChange={(v) => setActive(!!v)} />
            Active
          </label>
        </div>
      </div>

      <div className="mt-6 grid gap-5 md:grid-cols-3">
        <div>
          <Label htmlFor="sch-kw" className="text-xs uppercase tracking-wider text-muted-foreground">Primary keyword (optional)</Label>
          <Input id="sch-kw" className="mt-1.5" placeholder="hardwood floor refinishing Danbury CT" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="sch-hint" className="text-xs uppercase tracking-wider text-muted-foreground">Editorial hint (optional)</Label>
          <Textarea
            id="sch-hint"
            className="mt-1.5 min-h-[88px]"
            placeholder="Focus on luxury vinyl plank trends in Fairfield County and include local SEO keywords."
            value={hint}
            onChange={(e) => setHint(e.target.value)}
          />
        </div>
      </div>

      <div className="mt-6 flex gap-2">
        <Button onClick={save} disabled={saving}>
          {saving ? "Saving…" : editing ? "Save changes" : "Create schedule"}
        </Button>
        {editing && <Button variant="ghost" onClick={() => { reset(); onCancel(); }}>Cancel</Button>}
      </div>
    </div>
  );
}
